import { calculateChangeTracking } from 'Data/Core/ChangeTracker';
import { RootState } from 'Data/Redux';
import { concatLatest } from 'Lib/Rxjs/operators';
import copy from 'fast-clone';
import { Epic, ofType } from 'redux-observable';
import { ignoreElements, of, tap } from 'rxjs';
import { catchError, filter, map, mergeMap, retry } from 'rxjs/operators';
import { actions } from '.';
import webApi from '../../../Lib/WebApi';
import { sanitizeErrorObject } from '../../Helpers/errorHelpers';
import { addErrorNotice, callApi, camelizeObjectKeys, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { callApiGetConnections, callApiPutConnections } from './apiCalls';

export const getDatabaseConfigured: Epic = (action$) =>
  action$.pipe(
    ofType(actions.getDatabaseConfigured.toString()),
    mergeMap(() =>
      callApi(webApi.serviceSetup.databaseConfigured.get()).pipe(
        mergeMap(({ Body: { IsConfigured } }) => {
          if (!IsConfigured)
            return of(actions.getDatabaseConnection(), actions.getDatabaseConfiguredResolve(IsConfigured));
          return of(actions.getDatabaseConfiguredResolve(IsConfigured));
        }),
        catchError((err) =>
          of(
            actions.getDatabaseConfiguredReject(),
            addErrorNotice(
              'Failed to determine if the QEST Platform Connection has been setup. Please ensure that the QEST Web Api and QESTNet service are both running.',
              err
            )
          )
        )
      )
    )
  );

export const getDatabaseConnection: Epic = (action$) =>
  action$.pipe(
    ofType(actions.getDatabaseConnection.toString()),
    mergeMap(() =>
      callApi(webApi.serviceSetup.databaseConnection.get()).pipe(
        map((res) => actions.getDatabaseConnectionResolve(camelizeObjectKeys(res.Body))),
        catchError((err) => of(actions.getDatabaseConnectionReject()))
      )
    )
  );

export const testDatabaseConnection: Epic = (action$) =>
  action$.pipe(
    ofType(actions.testDatabaseConnection.toString()),
    mergeMap(() =>
      callApi(webApi.serviceSetup.databaseConnection.test.post()).pipe(
        map(() => actions.testDatabaseConnectionResolve()),
        catchError((err) => of(actions.testDatabaseConnectionReject(sanitizeErrorObject(err))))
      )
    )
  );

export const restartQestnet: Epic = (action$) =>
  action$.pipe(
    ofType(actions.restartQestnet.toString()),
    mergeMap(() =>
      callApi(webApi.serviceSetup.restartQestnet.post()).pipe(
        // Test running a safe endpoint to see if the service is back.
        // Retry 3 times (4 times total). It takes awhile between timeouts to give the service a chance to return.
        // If there is a legitimate error it won't return.
        mergeMap(() => callApi(webApi.env.get())),
        retry(3),
        tap(() => window.location.reload()),
        ignoreElements(),
        catchError((err) => of(actions.restartQestnetReject(sanitizeErrorObject(err))))
      )
    )
  );

export const syncOnChanges: Epic = (action$) =>
  action$.pipe(
    ofType(
      // Whenever a value changes
      actions.setValue.toString()
    ),
    filter(({ payload: { sync = false } = {} }) => !!sync), // only when sync is set to true
    mergeMap(() => of(actions.sync()))
  );

// This is our main synchronisation epic, that is responsible for keeping the server up-to-date with the clients changes.
// This runs like a service, continually (once at a time) updating the server state to look like the client state.
export const syncData: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.sync.toString()),
    concatLatest(() => {
      const stateValue: RootState = state$.value;
      const clientBefore = stateValue.serviceSetup.connection;
      const serverBefore = stateValue.serviceSetup.connection_synced;

      if (calculateChangeTracking(clientBefore, serverBefore).changeCount <= 0) return of(actions.syncResolve());

      const serverModelSent = copy(clientBefore);

      return callApiPutConnections({ Body: serverModelSent });
    }),
    // Run the sync and update our copy of the server model to what is received
    mergeMap(() => of(actions.syncResolve())),
    catchWithAuthErrors((error: any) => {
      return callApiGetConnections().pipe(
        mergeMap((serverModelReceived: any) =>
          // Repair succeeded, still report error, reset all state to server state.
          of(
            addErrorNotice('Failed to update connections.', error),
            actions.syncResolve({ syncPatch: serverModelReceived })
          )
        ),
        catchWithAuthErrors((recoveryError: any) =>
          // Repair failed, report additional error for failure and reset client state to last _known_ server state.
          of(
            addErrorNotice('Failed to update connections.', error),
            addErrorNotice(
              'Something went really wrong when attempting to retrieve the connections. Try refreshing the page.',
              recoveryError
            )
            //actions.syncResolve({ syncPatch: serverBefore })
          )
        )
      );
    })
  );
